import { useEffect, useRef } from "react";

import { PublicationStatus } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentSectionIsLinked,
  selectCurrentSectionIsTemplate,
  selectLatestPublicationForCurrentSection,
} from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";
import { useNavigate } from "utils/routing";
import { sectionHomeUrl, withQueryParams } from "utils/urls";

export const usePublishingObserver = () => {
  const { canAuthorSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isTemplate = useAppSelector(selectCurrentSectionIsTemplate);
  const isLinked = useAppSelector(selectCurrentSectionIsLinked);
  const latestPublication = useAppSelector(selectLatestPublicationForCurrentSection);
  const navigate = useNavigate();
  const prevStatusRef = useRef(latestPublication?.status);
  const prevSectionIdRef = useRef(sectionId);

  useEffect(() => {
    if (!canAuthorSection) {
      return;
    }

    if (prevSectionIdRef.current !== sectionId) {
      prevSectionIdRef.current = sectionId;
      prevStatusRef.current = latestPublication?.status;
      return;
    }

    if (
      prevStatusRef.current === PublicationStatus.STARTED &&
      latestPublication?.status === PublicationStatus.COMPLETED
    ) {
      if (isTemplate) {
        const publicationHistoryUrl = withQueryParams(sectionHomeUrl(sectionId), {
          tab: "publication_history",
        });

        toastSuccessMessage(
          <span className="flex items-center justify-between gap-3">
            {t("publishing.publish_success")}

            <Button kind="white" size="xs" onClick={() => navigate(publicationHistoryUrl)}>
              {t("publishing.view_log")}
            </Button>
          </span>,
          { closeButton: false },
        );
      } else if (isLinked) {
        toastSuccessMessage(t("publishing.section_updated_from_template"));
      }
    }
    prevStatusRef.current = latestPublication?.status;
  }, [canAuthorSection, isLinked, isTemplate, latestPublication?.status, navigate, sectionId]);
};
